import React, { useState, useCallback, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import { formatNumber } from "../../../../utils/formatting";
import { Bridge, BridgeType } from "../../../../types/bridge";
import {
  BRIDGE_TYPES,
  calculateBridgeCost,
  calculateBridgeWeight,
} from "../../../../constants/bridges";
import { setBridge } from "../../../../store/features/shipBuilderSlice";
import { setSectionBridge } from "../../../../store/features/breakawayHullSlice";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { formatCurrency } from "../../../../utils/formatting";

interface BreakawayBridgeConfigProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean; // If true, render inline instead of as modal
}

interface SectionBridge {
  sectionId: string;
  type: BridgeType;
  weight: number;
  cost: number;
  power: number;
  maintenanceCost: number;
  isAdequate: boolean;
}

export const BreakawayBridgeConfig: React.FC<BreakawayBridgeConfigProps> = ({
  isOpen,
  onClose,
  inline = false,
}) => {
  const dispatch = useAppDispatch();
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const techLevel = useAppSelector(selectors.selectTechLevel);
  const sizeOverrideActive = useAppSelector(selectors.selectSizeOverrideActive);
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get section bridges from Redux state (like main BridgeSelector)
  const sectionBridges: Record<string, SectionBridge> = useMemo(() => {
    const bridges: Record<string, SectionBridge> = {};
    if (breakawayHull?.sections) {
      breakawayHull.sections.forEach(section => {
        if (section.bridge) {
          bridges[section.id] = {
            sectionId: section.id,
            type: section.bridge.type,
            weight: section.bridge.weight,
            cost: section.bridge.cost,
            power: section.bridge.power,
            maintenanceCost: section.bridge.maintenanceCost,
            isAdequate: true, // Assume adequate if bridge is configured
          };
        }
      });
    }
    return bridges;
  }, [breakawayHull?.sections]);

  // Bridge availability validation (same as main BridgeSelector)
  const getBridgeAvailability = (bridgeType: BridgeType, sectionSize: number) => {
    const config = BRIDGE_TYPES[bridgeType];

    // Check tech level requirement
    if (config.techLevel > techLevel) {
      return {
        available: false,
        reason: `Requires Tech Level ${config.techLevel}`,
      };
    }

    // Check size requirements for this section
    if (!sizeOverrideActive && sectionSize < config.sizeRange.min) {
      return {
        available: false,
        reason: `Minimum section size: ${config.sizeRange.min} tons`,
      };
    }

    if (!sizeOverrideActive && sectionSize > config.sizeRange.max) {
      return {
        available: false,
        reason: `Maximum section size: ${config.sizeRange.max} tons`,
      };
    }

    return { available: true, reason: "" };
  };

  const handleBridgeChange = (sectionId: string, bridgeType: BridgeType) => {
    if (!breakawayHull?.sections) return;

    const section = breakawayHull.sections.find(s => s.id === sectionId);
    if (!section) return;

    // Validate bridge availability for this section
    const availability = getBridgeAvailability(bridgeType, section.size);
    if (!availability.available) {
      setErrors(prev => ({ ...prev, [sectionId]: availability.reason }));
      return;
    }

    const bridgeWeight = calculateBridgeWeight(bridgeType, section.size);
    const bridgeCost = calculateBridgeCost(bridgeType, section.size);
    const bridgeConfig = BRIDGE_TYPES[bridgeType];
    const maintenanceCost = Math.floor(bridgeCost * 0.01);

    // Immediately update Redux state (like main BridgeSelector)
    const sectionBridgeObj: Bridge = {
      type: bridgeType,
      cost: bridgeCost,
      weight: bridgeWeight,
      techLevel: bridgeConfig.techLevel,
      special: bridgeConfig.special,
      maintenanceCost: maintenanceCost,
      power: bridgeConfig.power,
      sizeRange: bridgeConfig.sizeRange,
      description: bridgeConfig.description,
      id: bridgeConfig.id,
    };

    dispatch(setSectionBridge({ sectionId, bridge: sectionBridgeObj }));

    // Clear error for this section
    setErrors(prev => ({ ...prev, [sectionId]: '' }));
  };


  const getTotalBridgeCost = useCallback(() => {
    return Object.values(sectionBridges).reduce((sum, bridge) => sum + bridge.cost, 0);
  }, [sectionBridges]);

  const getTotalBridgeWeight = useCallback(() => {
    return Object.values(sectionBridges).reduce((sum, bridge) => sum + bridge.weight, 0);
  }, [sectionBridges]);

  const getTotalBridgePower = useCallback(() => {
    return Object.values(sectionBridges).reduce((sum, bridge) => sum + bridge.power, 0);
  }, [sectionBridges]);

  const handleSave = useCallback(() => {
    // Check for errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      return;
    }

    // Update the main bridge with combined values (for summary display)
    const totalCost = getTotalBridgeCost();
    const totalWeight = getTotalBridgeWeight();
    const totalPower = getTotalBridgePower();
    const totalMaintenance = Object.values(sectionBridges).reduce((sum, bridge) => sum + bridge.maintenanceCost, 0);

    // Use the most common bridge type, or standard as default
    const typeCounts: Record<BridgeType, number> = {} as Record<BridgeType, number>;
    Object.values(sectionBridges).forEach(bridge => {
      typeCounts[bridge.type] = (typeCounts[bridge.type] || 0) + 1;
    });
    
    const typeEntries = Object.entries(typeCounts);
    const mostCommonType = typeEntries.length > 0 
      ? typeEntries.reduce((a, b) => a[1] > b[1] ? a : b)[0] as BridgeType
      : 'STANDARD' as BridgeType;

    const updatedBridge: Bridge = {
      type: mostCommonType,
      cost: totalCost,
      weight: totalWeight,
      techLevel: BRIDGE_TYPES[mostCommonType].techLevel,
      special: BRIDGE_TYPES[mostCommonType].special,
      maintenanceCost: totalMaintenance,
      power: totalPower,
      sizeRange: BRIDGE_TYPES[mostCommonType].sizeRange,
      description: BRIDGE_TYPES[mostCommonType].description,
      id: BRIDGE_TYPES[mostCommonType].id,
    };

    dispatch(setBridge(updatedBridge));

    if (!inline) {
      onClose();
    }
  }, [inline, onClose, errors, sectionBridges, dispatch, getTotalBridgeCost, getTotalBridgeWeight, getTotalBridgePower]);


  if (!isOpen || !breakawayHull?.sections) return null;

  const totalBridgeCost = getTotalBridgeCost();
  const totalBridgeWeight = getTotalBridgeWeight();
  const totalBridgePower = getTotalBridgePower();

  const content = (
    <div className={inline ? "w-full" : "bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
      {!inline && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Breakaway Bridge Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="mb-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Breakaway Bridge Rules</h3>
          <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
            <li>• Each breakaway section can have its own bridge for independent operation</li>
            <li>• When sections are together, the main bridge provides overall ship control</li>
            <li>• Bridge requirements are calculated based on each section's size</li>
            <li>• Each section's bridge must meet that section's operational requirements</li>
            <li>• Combined bridge statistics are calculated for the main ship</li>
          </ul>
        </div>
      </div>


      {/* Section Bridge Configuration */}
      <div className="space-y-6">
        {breakawayHull.sections.map((section) => {
          const sectionBridge = sectionBridges[section.id] || {
            sectionId: section.id,
            type: 'STANDARD' as BridgeType,
            weight: 0,
            cost: 0,
            power: 0,
            maintenanceCost: 0,
            isAdequate: false,
          };
          const error = errors[section.id];

          return (
            <div key={section.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                {section.name} ({section.size} tons)
              </h4>

              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm mb-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bridge Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bridge Type
                  </label>
                  <select
                    value={sectionBridge.type}
                    onChange={(e) => handleBridgeChange(section.id, e.target.value as BridgeType)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {Object.entries(BRIDGE_TYPES).map(([key, config]) => {
                      const availability = getBridgeAvailability(key as BridgeType, section.size);
                      return (
                        <option 
                          key={key} 
                          value={key}
                          disabled={!availability.available}
                        >
                          {config.description} {!availability.available ? `(${availability.reason})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Bridge Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bridge Weight
                  </label>
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                    {formatNumber(sectionBridge.weight)} tons
                  </div>
                </div>
              </div>

              {/* Bridge Details */}
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="mb-2">
                  <strong>Bridge Details:</strong>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Cost: {formatCurrency(sectionBridge.cost)}</div>
                  <div>Power: {formatNumber(sectionBridge.power)} Power</div>
                  <div>Maintenance: {formatCurrency(sectionBridge.maintenanceCost)}/month</div>
                  <div>Tech Level: {BRIDGE_TYPES[sectionBridge.type].techLevel}</div>
                </div>
                {BRIDGE_TYPES[sectionBridge.type].special && (
                  <div className="mt-2 text-xs text-purple-600 dark:text-purple-400">
                    <strong>Special:</strong> {BRIDGE_TYPES[sectionBridge.type].special}
                  </div>
                )}
              </div>

              {/* Adequacy Status */}
              <div className={`mt-2 text-sm font-medium ${sectionBridge.isAdequate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {sectionBridge.isAdequate ? '✓ Bridge adequate for section requirements' : '✗ Bridge inadequate for section requirements'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Combined Bridge Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Bridge Weight:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalBridgeWeight)} tons</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Bridge Cost:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(totalBridgeCost)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Power Required:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalBridgePower)} Power</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Maintenance:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(Object.values(sectionBridges).reduce((sum, bridge) => sum + bridge.maintenanceCost, 0))}/month</span>
          </div>
        </div>
      </div>

      {/* General Errors */}
      {errors.general && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
          <p className="text-sm text-red-700 dark:text-red-300">{errors.general}</p>
        </div>
      )}

      {!inline && (
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={Object.values(errors).some(error => error !== '')}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {content}
    </div>
  );
}; 